/**
 * FROZEN CONTRACT — Pokemon elemental types and the type-effectiveness chart.
 *
 * These shapes are stable across every layer (core/society/lore/scenes). The
 * battle engine (core/) fills in behavior; this file owns the *data shapes* and
 * the canonical multiplier table.
 */

/** The 18 canonical elemental types (Gen 6+ chart, including Fairy). */
export type PokemonType =
  | 'normal'
  | 'fire'
  | 'water'
  | 'electric'
  | 'grass'
  | 'ice'
  | 'fighting'
  | 'poison'
  | 'ground'
  | 'flying'
  | 'psychic'
  | 'bug'
  | 'rock'
  | 'ghost'
  | 'dragon'
  | 'dark'
  | 'steel'
  | 'fairy';

/** Ordered tuple of all 18 types — stable iteration order for the chart. */
export const POKEMON_TYPES = [
  'normal',
  'fire',
  'water',
  'electric',
  'grass',
  'ice',
  'fighting',
  'poison',
  'ground',
  'flying',
  'psychic',
  'bug',
  'rock',
  'ghost',
  'dragon',
  'dark',
  'steel',
  'fairy',
] as const satisfies readonly PokemonType[];

/**
 * A single attacking-vs-defending multiplier. The standard chart only ever uses
 * 0 (immune), 0.5 (not very effective), 1 (neutral), and 2 (super effective).
 */
export type TypeMultiplier = 0 | 0.5 | 1 | 2;

/**
 * The full type chart, indexed `[attackingType][defendingType]`.
 *
 * To compute a move's type effectiveness against a (possibly dual-typed) target,
 * the engine multiplies the move type's multiplier against EACH of the target's
 * types: `mult = chart[moveType][def1] * (def2 ? chart[moveType][def2] : 1)`.
 * Result is in {0, 0.25, 0.5, 1, 2, 4}.
 */
export type TypeChart = Readonly<
  Record<PokemonType, Readonly<Record<PokemonType, TypeMultiplier>>>
>;
